import { useEffect, useState } from 'react';

interface Props {
  message: string;
  type?: 'success' | 'error';
  onClose: () => void;
}

export default function Notification({ message, type = 'success', onClose }: Props) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    requestAnimationFrame(() => setVisible(true));

    // Auto close after 2.5s
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(onClose, 300);
    }, 2500);

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      className={`notification notification-${type} ${visible ? 'visible' : ''} ${exiting ? 'exiting' : ''}`}
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: `translateX(-50%) translateY(${visible && !exiting ? '0' : '80px'})`,
        padding: '14px 28px',
        background: type === 'error' ? '#dc2626' : '#1a1a1a',
        color: 'white',
        borderRadius: '100px',
        fontSize: '0.9rem',
        fontWeight: 500,
        zIndex: 9999,
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        opacity: exiting ? 0 : 1,
      }}
    >
      {message}
    </div>
  );
}

// Hook for managing notifications
export function useNotification() {
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
  };

  const hideNotification = () => {
    setNotification(null);
  };

  const NotificationComponent = notification ? (
    <Notification
      message={notification.message}
      type={notification.type}
      onClose={hideNotification}
    />
  ) : null;

  return { showNotification, NotificationComponent };
}
